/**
 * Places (`/b/:brandId/places`): the list. Staff see every place, brand-owned
 * and house places side by side; a brand's staff see every place their brand
 * owns, oldest first, the default one marked.
 * Read-only — a row opens "the place today".
 */
import { AlertCircle, ChevronRight, Loader2, MapPin } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";

import type { PlaceListData } from "@/data/places-api";
import { usePortal } from "@/features/portal/portal-context";
import { placePath } from "@/features/portal/navigation";
import { fixtureQuery } from "@/features/places/fixtures";
import { headcountLabel, headcountOf, PillBadge, PresenceDot, SampleChip } from "@/features/places/place-today";
import { usePlaces } from "@/features/places/use-places";
import { Card } from "@/components/ui/card";

export function PlacesLoading({ what }: { what: string }) {
  return (
    <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> Reading {what}…
    </div>
  );
}

export function PlacesError({ message, title = "Could not read places" }: { message: string; title?: string }) {
  return (
    <Card className="flex items-start gap-3 p-5">
      <AlertCircle className="mt-0.5 size-5 shrink-0 text-[#ff7a7a]" />
      <div>
        <div className="text-[14px] font-bold">{title}</div>
        <p className="mt-0.5 text-[13px] text-muted-foreground">{message}</p>
      </div>
    </Card>
  );
}

function PlaceRow({ place, to, sample }: { place: PlaceListData; to: string; sample: boolean }) {
  return (
    <li>
      <Link to={to} className="flex items-center gap-3.5 px-5 py-3.5 transition-colors hover:bg-panel-2">
        <div className="grid size-9 shrink-0 place-items-center rounded-[10px] border border-border bg-panel-2">
          <MapPin className="size-4 text-brand" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[14.5px] font-bold">{place.name}</span>
            {place.isDefault && <PillBadge>Default</PillBadge>}
          </div>
          <div className="text-[11.5px] text-text-3">
            {place.brand ? `Belongs to ${place.brand.name}` : "House place"} · {place.subGatherings.totalCount}{" "}
            {place.subGatherings.totalCount === 1 ? "table" : "tables"} open
          </div>
        </div>
        <div className="flex items-center gap-2 text-[13px] font-semibold tabular-nums">
          <PresenceDot count={headcountOf(place)} />
          {headcountLabel(place)} here
          {sample && <SampleChip />}
        </div>
        <ChevronRight className="size-4 text-text-3" />
      </Link>
    </li>
  );
}

export function PlacesPage() {
  // The URL is the source of truth for the brand (see `navigation.ts`); the
  // portal context catches up a render later.
  const { brandId = "" } = useParams();
  const { activeBrand, isSuperAdmin } = usePortal();
  const carry = fixtureQuery(useLocation().search);
  const load = usePlaces(brandId);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-extrabold tracking-[-.02em]">Places</h1>
        <p className="mt-1 text-[13.5px] text-muted-foreground">
          {isSuperAdmin
            ? "Every place on pop.in: each brand's own, and the house places."
            : `Where ${activeBrand?.name ?? "your brand"}'s players gather.`}
        </p>
      </div>

      {load.state === "loading" && <PlacesLoading what="places" />}
      {load.state === "error" && <PlacesError message={load.message} />}
      {load.state === "ready" &&
        (load.data.length === 0 ? (
          <Card className="p-5 text-[13px] text-muted-foreground">
            No place yet. A brand's place appears here a moment after the brand is created.
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <ul className="divide-y divide-border">
              {load.data.map((p) => (
                // Keep ?fixtures=1 (only) so the sample flow survives the click.
                <PlaceRow key={p.id} place={p} to={placePath(brandId, p.id) + carry} sample={load.sample} />
              ))}
            </ul>
          </Card>
        ))}
    </div>
  );
}
